import React from "react";
import {
  Box,
  Image,
  Text,
  Heading,
  IconButton,
  HStack,
  VStack,
  Badge,
  Input,
  Button,
} from "@chakra-ui/react";
import {
  FaFacebookF,
  FaEnvelope,
  FaWhatsapp,
  FaLinkedin,
} from "react-icons/fa"; // Import share icons
import { ReactComponent as TwitterIcon } from "../../assets/icons/twitter.svg";
import Navbar from "../../components/Navbar";
import darkLogo from "../../assets/images/Newlogo-black.png";
import BackToTopButton from "../common/BackToTopButton";
import Footer from "../../components/Footer";
import { useTranslation } from "../../components/LanguageTranslation/TranslationContext";
import Translate from "../../components/LanguageTranslation/Translate";
import InstagramFeed from "./InstagramFeed";

const BlogPreview = () => {
  const { useTranslate } = useTranslation();

  let blog = null;
  try {
    const raw = localStorage.getItem("BLOG_PREVIEW_DATA");
    if (raw) {
      blog = JSON.parse(raw);
      // console.log("Parsed blog data:", blog);
    } else {
      // console.log("No blog data found in localStorage");
    }
  } catch (e) {
    console.error("Error parsing blog data:", e);
  }

  if (!blog && typeof window !== "undefined" && window.blogData) {
    blog = window.blogData;
    // console.log("Using window.blogData:", blog);
  }

  const normalizedBlog = blog
    ? {
        ...blog,
        categories: (blog.categories || []).map((c) =>
          typeof c === "string"
            ? {
                category_name: c,
                category_value_list: (blog.values || {})[c] || [],
              }
            : c
        ),
      }
    : {
        seoTitle: "Sample Blog Title",
        content: "<p>This is a sample blog content. If you're seeing this, it means the preview data is not available. Please try clicking the Preview button again from the blog upload form.</p>",
        categories: [],
        creation_date: new Date().toISOString(),
        previewImageDataURLs: [],
        imageURLs: [],
      };
  const placeholderText = useTranslate("Enter email");

  return (
    <Box>
      <Navbar bgColor="white" linkColor="black" styling="outline" logoSrc={darkLogo} userProfileBgColor="transparaent"/>
      {!blog && (
        <Box p={4} bg="yellow.100" textAlign="center">
          <Text color="orange.800" fontWeight="bold">
            Preview data not found. Please go back to the blog upload form and click "Preview Blog" again.
          </Text>
          <Button 
            mt={2} 
            size="sm" 
            onClick={() => {
              localStorage.removeItem("BLOG_PREVIEW_DATA");
              window.location.reload();
            }}
          >
            Clear Preview Data
          </Button>
        </Box>
      )}
      <Box
        w="100%"
        mx="auto"
        px={4}
        py={8}
        fontFamily="Poppins, sans-serif" // Set the font for the entire page
      >
        {/* Center-aligned Blog Box */}
        <Box display="flex" justifyContent="center" w="100%">
          <Box
            maxW={{ base: "100vw", sm: "85vw", md: "85vw", lg: "60vw" }}
            mx="auto"
            textAlign="left" // Ensures text inside the Box is left aligned
          >
            <VStack spacing={4} align="stretch">
              {/* Blog Header */}
              <HStack spacing={2} mb={2}>
                {normalizedBlog.categories.length > 0 ? (
                  normalizedBlog.categories.map((cat) => (
                    <Badge
                      key={cat.category_name}
                      bg="black"
                      variant="solid"
                      borderRadius="md"
                      fontSize="md"
                      p={4}
                      pt={1}
                      pb={1}
                      textTransform="none"
                    >
                      {cat.category_value_list.join(", ")}
                    </Badge>
                  ))
                ) : (
                  <Badge
                    bg="black"
                    variant="solid"
                    borderRadius="md"
                    fontSize="md"
                    p={4}
                    pt={1}
                    pb={1}
                    textTransform="none"
                  >
                    General
                  </Badge>
                )}
                <Text fontSize="sm" color="gray.500" mb={0}>
                  {normalizedBlog.creation_date
                    ? new Date(normalizedBlog.creation_date).toLocaleDateString("en-US", {
                        day: "2-digit",
                        month: "short",
                        year: "numeric",
                      })
                    : "N/A"}
                </Text>
              </HStack>

              {/* Blog Title */}
              <Heading
                as="h1"
                mt={4}
                mb={4}
                fontSize={{ base: "30px", sm: "48px", md: "48px" }}
                fontWeight="bold"
                textAlign="left" // Aligning the title to the left
              >
                <Translate text={normalizedBlog.seoTitle} />
              </Heading>

              {/* Blog Image */}
              {(normalizedBlog.previewImageDataURLs && normalizedBlog.previewImageDataURLs.length > 0) ? (
                <Image
                  src={normalizedBlog.previewImageDataURLs[0]}
                  alt={normalizedBlog.seoTitle}
                  objectFit="cover"
                  width="800px"
                  height={{ base: "200px", sm: "300px", md: "400px" }}
                  borderRadius="md"
                  mb={8}
                />
              ) : (normalizedBlog.imageURLs && normalizedBlog.imageURLs.length > 0) ? (
                <Image
                  src={normalizedBlog.imageURLs[0]}
                  alt={normalizedBlog.seoTitle}
                  objectFit="cover"
                  width="800px"
                  height={{ base: "200px", sm: "300px", md: "400px" }}
                  borderRadius="md"
                  mb={8}
                />
              ) : null}
              {/* Blog Content */}
              <Box
                fontSize={{ base: "md", sm: "lg", md: "xl" }}
                lineHeight="tall"
                textAlign="left" // Ensure the blog content is left-aligned
                dangerouslySetInnerHTML={{ __html: normalizedBlog.content }} // Rendering HTML content
              />
            </VStack>
          </Box>
        </Box>

        {/* Share Section */}
        <Box mt={8} mb={8} textAlign="center">
          <Text fontSize="lg" mb={4} fontWeight="bold">
            <Translate text="Share" />
          </Text>
          <HStack spacing={4} justify="center">
            <IconButton
              icon={<FaFacebookF />}
              aria-label="Share on Facebook"
              bg="black"
              color="white"
              as="a"
            />
            <IconButton
              icon={
                <TwitterIcon
                  style={{ width: "20px", height: "20px", borderRadius: "xl" }}
                />
              } // Set the size of the SVG icon
              // icon={<FaTwitter />}
              aria-label="Share on Twitter"
              bg="black"
              color="white"
              _active="none"
              _hover="none"
              as="a"
            />
            <IconButton
              icon={<FaLinkedin />}
              aria-label="Share on LinkedIn"
              bg="black"
              color="white"
              _active="none"
              _hover="none"
              as="a"
            />

            <IconButton
              icon={<FaWhatsapp />}
              aria-label="Share on WhatsApp"
              bg="black"
              color="white"
              _active="none"
              _hover="none"
              as="a"
            />
            <IconButton
              icon={<FaEnvelope />}
              aria-label="Share via Email"
              bg="black"
              color="white"
              _active="none"
              _hover="none"
              as="a"
            />
          </HStack>
        </Box>
        
        <Heading
        as="h1"
        fontSize={{ base: "2xl", md: "4xl", lg: "5xl" }}
        fontWeight="bold"
        mt={7}
        mb={7}
        pl={{ base: 4, md: 16 }}
        pr={{ base: 4, md: 16 }}
        fontFamily="Poppins, sans-serif"
      >
        <Translate text="You may also like" />
      </Heading>

        {/* Newsletter Section */}
        <Box
          mt={12}
          // mb={8}
          p={8}
          pb
          bg="#F7F7F7"
          borderRadius="lg"
          textAlign="center"
        >
          <Heading as="h2" size="lg" mb={4}>
            <Translate text="Join the Green & Wild Community!" />
          </Heading>
          <Text
            mb={6}
            w={{ base: "100%", sm: "100%", md: "50%" }}
            mx="auto"
            textAlign="center"
          >
            <Translate
              text="Subscribe to NOMAD360' newsletter for personalized travel tips,
            epic outdoor experiences, and exclusive offers. Connect with fellow
            explorers and start planning your next adventure today!"
            />
          </Text>
          <HStack
            maxW="500px"
            mx="auto"
            p={2}
            borderRadius="md"
            bg="white"
            boxShadow="sm"
            spacing={4}
          >
            <Input
              type="email"
              placeholder={placeholderText}
              variant="unstyled"
              px={4}
              flex="1"
            />
            <Button
              bg="black"
              color="white"
              // borderRadius="full"
              px={6}
              _hover={{ bg: "gray.800" }}
            >
              <Translate text="Subscribe" />
            </Button>
          </HStack>
        </Box>
        <Box w="96vw" mx="auto">
        <InstagramFeed />
        </Box>
      </Box>
      <Footer />
      <BackToTopButton />
    </Box>
  );
};

export default BlogPreview;
